import { MyUsageTrendsResponse, OrgUsageTrendsResponse } from "@/pages/credits/types";
import { Stack } from "@altimateai/lego";
import dayjs from "dayjs";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useMemo } from "react";

interface UsageTrendChartProps {
  data: MyUsageTrendsResponse["graph_data"] | OrgUsageTrendsResponse["graph_data"];
  startDate: Date;
  endDate: Date;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Stack
        direction={"column"}
        className="al-bg-white al-p-4 al-border al-border-gray-200 al-rounded al-shadow-lg"
      >
        <p className="al-font-bold">{label}</p>
        <p>Total Credits Consumed: {data.total_credits_consumed}</p>
      </Stack>
    );
  }

  return null;
};

const RoundedBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  const radius = 5;

  // Don't render anything if height is 0 or very small (less than 1 pixel)
  if (height <= 1) {
    return null;
  }

  return (
    <g>
      <path
        d={`M${x},${y + radius} Q${x},${y} ${x + radius},${y} L${x + width - radius},${y} Q${x + width},${y} ${x + width},${y + radius} L${x + width},${y + height} L${x},${y + height} Z`}
        fill={fill}
      />
    </g>
  );
};

export const UsageTrendChart = ({ data, startDate, endDate }: UsageTrendChartProps) => {
  const formattedData = useMemo(
    () =>
      data.map(item => ({
        ...item,
        date:
          dayjs(endDate).diff(dayjs(startDate), "day") > 31
            ? dayjs(item.date).format("MMM YYYY")
            : dayjs(item.date).format("DD MMM"),
      })),
    [data, startDate, endDate]
  );

  if (data.length === 0) {
    return (
      <Stack justify="center" align="center" className="al-h-32 al-text-gray-500">
        No usage data available
      </Stack>
    );
  }

  return (
    <div className="al-h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={formattedData}
          margin={{
            right: 15,
            left: 15,
          }}
        >
          <XAxis dataKey="date" />
          <YAxis tick={false} hide={true} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="total_credits_consumed" fill="#247efe" shape={<RoundedBar />} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
